import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { Card } from '../ui';

type QuickRange = 'mtd' | 'last30' | 'last90' | 'ytd';

const RANGE_TITLES: Record<QuickRange, string> = {
  mtd: 'so far',
  last30: 'Last 30 days',
  last90: 'Last 90 days',
  ytd: 'Year to date',
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/** Animated margin ring — stroke-dashoffset eases to the current %. */
function MarginRing({ pct }: { pct: number }) {
  const r = 44;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" style={{ flexShrink: 0 }}>
      <circle cx="60" cy="60" r={r} fill="none" stroke="var(--surface-active)" strokeWidth="10" />
      <circle
        cx="60"
        cy="60"
        r={r}
        fill="none"
        stroke="var(--chart-1)"
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - clamped / 100)}
        transform="rotate(-90 60 60)"
        style={{ transition: 'stroke-dashoffset var(--dur-page) var(--ease-out)' }}
      />
      <text
        x="60"
        y="66"
        textAnchor="middle"
        style={{ fill: 'var(--text-body)', font: '600 22px var(--font-mono)', letterSpacing: '-0.02em' }}
      >
        {pct}%
      </text>
    </svg>
  );
}

export default function MarginCard() {
  const [isLoading, setIsLoading] = useState(false);
  const [activeRange, setActiveRange] = useState<QuickRange | 'custom'>('mtd');
  const [marginData, setMarginData] = useState({
    revenue: 0,
    expenses: 0,
    profit: 0,
    margin: 0,
  });

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

  const calculateMargin = useCallback(async () => {
    setIsLoading(true);
    try {
      // Parse dates correctly to avoid timezone issues
      const [startYear, startMonthStr, startDay] = startDate.split('-').map(Number);
      const [endYear, endMonthStr, endDay] = endDate.split('-').map(Number);

      const start = new Date(startYear, startMonthStr - 1, startDay);
      const end = new Date(endYear, endMonthStr - 1, endDay, 23, 59, 59, 999);

      const [transactionsResult, expensesResult] = await Promise.all([
        supabase
          .from('stripe_transactions')
          .select('amount, created_at')
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString())
          .gt('amount', 0),
        supabase.from('financial_expenses').select('amount, month, year'),
      ]);

      if (transactionsResult.error) throw transactionsResult.error;
      if (expensesResult.error) throw expensesResult.error;

      const revenue = transactionsResult.data?.reduce((sum, txn) => sum + txn.amount / 100, 0) || 0;

      // Only include expenses for months within the range
      const relevantExpenses = expensesResult.data?.filter((exp) => {
        const expDate = exp.year * 100 + exp.month;
        const rangeStart = startYear * 100 + startMonthStr;
        const rangeEnd = endYear * 100 + endMonthStr;
        return expDate >= rangeStart && expDate <= rangeEnd;
      });

      const expenses = relevantExpenses?.reduce((sum, exp) => sum + Number(exp.amount), 0) || 0;

      const profit = revenue - expenses;
      const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

      setMarginData({ revenue, expenses, profit, margin });
    } catch (err) {
      console.error('Error calculating margin:', err);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    calculateMargin();
  }, [calculateMargin]);

  const handleQuickRange = (range: QuickRange) => {
    const today = new Date();
    let start = new Date();

    switch (range) {
      case 'mtd':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'last30':
        start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'last90':
        start = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case 'ytd':
        start = new Date(today.getFullYear(), 0, 1);
        break;
    }

    setActiveRange(range);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  const monthName = today.toLocaleDateString('en-US', { month: 'long' });
  const title =
    activeRange === 'mtd'
      ? `${monthName}, so far`
      : activeRange === 'custom'
        ? 'Custom range'
        : RANGE_TITLES[activeRange];

  return (
    <Card eyebrow="Margin" title={title}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '18px',
          opacity: isLoading ? 0.55 : 1,
          transition: 'opacity var(--dur-base) var(--ease-out)',
        }}
      >
        <MarginRing pct={Math.round(marginData.margin)} />
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <div>
            <span className="t-num" style={{ color: 'var(--status-up)' }}>
              +{formatCurrency(marginData.revenue)}
            </span>{' '}
            in
          </div>
          <div>
            <span className="t-num" style={{ color: 'var(--text-body)' }}>
              −{formatCurrency(marginData.expenses)}
            </span>{' '}
            out
          </div>
          <div>
            <span
              className="t-num"
              style={{ color: marginData.profit >= 0 ? 'var(--status-up)' : 'var(--status-down)' }}
            >
              {formatCurrency(marginData.profit)}
            </span>{' '}
            net
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        {(
          [
            { label: 'MTD', value: 'mtd' },
            { label: '30d', value: 'last30' },
            { label: '90d', value: 'last90' },
            { label: 'YTD', value: 'ytd' },
          ] as Array<{ label: string; value: QuickRange }>
        ).map((range) => {
          const isActive = activeRange === range.value;
          return (
            <button
              key={range.value}
              onClick={() => handleQuickRange(range.value)}
              className="t-num pressable hover-wash"
              style={{
                flex: 1,
                height: 'var(--control-sm)',
                borderRadius: 'var(--radius-sm)',
                border: `1px solid ${isActive ? 'var(--border-strong)' : 'var(--border-hairline)'}`,
                background: isActive ? 'var(--surface-active)' : 'transparent',
                color: isActive ? 'var(--text-body)' : 'var(--text-muted)',
                fontSize: 'var(--text-xs)',
                cursor: 'pointer',
                transition: 'border-color var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)',
              }}
            >
              {range.label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: 'grid',
          // minmax(0, …): a date input's intrinsic width otherwise pushes the card past a 375px screen
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-3)',
        }}
      >
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>
            From
          </div>
          <input
            type="date"
            className="ck-date-input"
            style={{ minWidth: 0 }}
            value={startDate}
            max={endDate}
            onChange={(e) => {
              setActiveRange('custom');
              setStartDate(e.target.value);
            }}
          />
        </div>
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>
            To
          </div>
          <input
            type="date"
            className="ck-date-input"
            style={{ minWidth: 0 }}
            value={endDate}
            min={startDate}
            onChange={(e) => {
              setActiveRange('custom');
              setEndDate(e.target.value);
            }}
          />
        </div>
      </div>
    </Card>
  );
}
